import { useRef, useEffect, useState, useCallback } from 'react';

// Vídeos padrão (transição 1 é fixa; 2 e 3 são substituídos dinamicamente)
const DEFAULT_VIDEOS = [
  '/mf-lancamentos/transicao_1.mp4',
  '/mf-lancamentos/transicao_2.mp4',
  '/mf-lancamentos/transicao_3.mp4'
];

export default function useVideoScrub() {
  const video1Ref = useRef(null);
  const video2Ref = useRef(null);
  const video3Ref = useRef(null);
  const introVideoRef = useRef(null);
  const scrollTrackRef = useRef(null);

  const [videoDurations, setVideoDurations] = useState(DEFAULT_VIDEOS.map(() => 0));
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasStartedScrolling, setHasStartedScrolling] = useState(false);
  const [introVideoLoaded, setIntroVideoLoaded] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videosLoadedCount, setVideosLoadedCount] = useState(0);
  const [isDraggingTrack, setIsDraggingTrack] = useState(false);
  const [videosReady, setVideosReady] = useState(DEFAULT_VIDEOS.map(() => false));
  const [previousVideoIndex, setPreviousVideoIndex] = useState(0);

  // === NOVO: Quiz lock system ===
  // Indica qual pergunta do quiz deve ser exibida (-1 = nenhuma, 0/1/2 = pergunta correspondente)
  const [activeQuizQuestion, setActiveQuizQuestion] = useState(-1);
  // Nudge: quando o usuário tenta scrollar mas está travado
  const [showNudge, setShowNudge] = useState(false);
  // Flag: todas as perguntas foram respondidas, vídeo final terminou
  const [quizCompleted, setQuizCompleted] = useState(false);

  // Ref para ler videoDurations atuais de dentro de timeouts/closures
  const videoDurationsRef = useRef(videoDurations);
  videoDurationsRef.current = videoDurations;

  // Refs para controle de reprodução
  const targetTimeRef = useRef(0);
  const animationFrameRef = useRef(null);
  const isPlayingRef = useRef(DEFAULT_VIDEOS.map(() => false));
  const currentVideoIndexRef = useRef(0);
  const isInitializedRef = useRef(false);
  const lastVisibleVideoRef = useRef(0);

  // Refs para controle de snap nas transições
  const lastScrollYRef = useRef(0);
  const isSnappingRef = useRef(false);
  const snapTimeoutRef = useRef(null);
  const allowedTransitionRef = useRef(0);
  const lastTransitionIndexRef = useRef(0);

  // Ref para lock do quiz (ref para acesso síncrono nos handlers de scroll)
  const isQuizLockedRef = useRef(false);
  const nudgeTimeoutRef = useRef(null);
  // Ref para snapToTime — resolve dependência circular com unlockAndAdvance
  const snapToTimeRef = useRef(null);
  // Ref para lockForQuiz — resolve dependência circular com handleFirstScroll
  const lockForQuizRef = useRef(null);
  // Ref para rastrear quais perguntas já foram respondidas
  const answeredQuestionsRef = useRef(new Set());
  // Ref para saber se o quiz 0 já foi exibido (antes do scroll começar)
  const firstQuizShownRef = useRef(false);
  // Ref para guardar o currentTime do intro quando o quiz 0 apareceu
  const introTimeAtQuizRef = useRef(0);

  // Multiplicador para a altura do scroll
  const scrollMultiplier = 0.25;
  const totalDuration = videoDurations.reduce((sum, d) => sum + d, 0);
  const scrollHeight = totalDuration * scrollMultiplier * window.innerHeight;

  // Força o scroll para o topo e desabilita scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
      isInitializedRef.current = true;
    }, 50);
    return () => {
      clearTimeout(timeoutId);
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  const getVideoRef = useCallback((index) => {
    if (index === 0) return video1Ref;
    if (index === 1) return video2Ref;
    return video3Ref;
  }, []);

  const handleLoadedMetadata = useCallback((index) => {
    const videoRef = getVideoRef(index);
    const video = videoRef.current;
    if (video) {
      setVideoDurations(prev => {
        const newDurations = [...prev];
        newDurations[index] = video.duration;
        return newDurations;
      });
      video.pause();
      setVideosLoadedCount(prev => prev + 1);
    }
  }, [getVideoRef]);

  const handleCanPlayThrough = useCallback((index) => {
    setVideosReady(prev => {
      const newReady = [...prev];
      newReady[index] = true;
      return newReady;
    });
  }, []);

  // Força pré-carregamento agressivo dos vídeos em iOS
  useEffect(() => {
    const preloadVideos = async () => {
      const videoRefs = [video1Ref, video2Ref, video3Ref];
      for (let i = 0; i < videoRefs.length; i++) {
        const video = videoRefs[i].current;
        if (video) {
          try {
            video.muted = true;
            video.currentTime = 0;
            await video.play();
            video.pause();
            video.currentTime = 0;
          } catch (e) {
            // Ignora erros - apenas tentativa de pré-carregamento
          }
        }
      }
    };
    const timeoutId = setTimeout(preloadVideos, 500);
    return () => clearTimeout(timeoutId);
  }, []);

  // Marca como carregado quando todos os vídeos estiverem prontos
  useEffect(() => {
    if (videosLoadedCount === DEFAULT_VIDEOS.length && totalDuration > 0) {
      setIsLoaded(true);
    }
  }, [videosLoadedCount, totalDuration]);

  // Calcula qual vídeo e tempo baseado no tempo global
  const getVideoAndTime = useCallback((globalTime) => {
    let accumulated = 0;
    for (let i = 0; i < videoDurations.length; i++) {
      const duration = videoDurations[i];
      if (globalTime <= accumulated + duration) {
        return { videoIndex: i, localTime: globalTime - accumulated };
      }
      accumulated += duration;
    }
    const lastIndex = videoDurations.length - 1;
    return { videoIndex: lastIndex, localTime: videoDurations[lastIndex] };
  }, [videoDurations]);

  // Loop de animação que reproduz o vídeo até o tempo alvo
  const animate = useCallback(() => {
    if (!isLoaded || totalDuration === 0) {
      animationFrameRef.current = requestAnimationFrame(animate);
      return;
    }

    const globalTargetTime = targetTimeRef.current;
    const { videoIndex, localTime: targetLocalTime } = getVideoAndTime(globalTargetTime);

    // Atualiza o índice do vídeo atual se mudou
    if (videoIndex !== currentVideoIndexRef.current) {
      const prevIndex = currentVideoIndexRef.current;
      const nextVideoRef = getVideoRef(videoIndex);
      const nextVideo = nextVideoRef.current;
      const isNextReady = nextVideo && nextVideo.readyState >= 3;

      if (isNextReady) {
        setPreviousVideoIndex(prevIndex);
        lastVisibleVideoRef.current = prevIndex;
      }

      const prevVideoRef = getVideoRef(prevIndex);
      if (prevVideoRef.current) {
        prevVideoRef.current.pause();
        isPlayingRef.current[prevIndex] = false;
      }
      currentVideoIndexRef.current = videoIndex;
      setCurrentVideoIndex(videoIndex);
    }

    const videoRef = getVideoRef(videoIndex);
    const video = videoRef.current;
    if (!video) {
      animationFrameRef.current = requestAnimationFrame(animate);
      return;
    }

    const currentTime = video.currentTime;
    const diff = targetLocalTime - currentTime;
    const absDiff = Math.abs(diff);

    if (absDiff < 0.03) {
      if (isPlayingRef.current[videoIndex]) {
        video.pause();
        isPlayingRef.current[videoIndex] = false;
      }
      animationFrameRef.current = requestAnimationFrame(animate);
      return;
    }

    const baseSpeed = Math.min(Math.max(absDiff * 3, 0.5), 8);

    if (diff > 0) {
      video.playbackRate = baseSpeed;
      if (!isPlayingRef.current[videoIndex] || video.paused) {
        video.play().catch(() => {});
        isPlayingRef.current[videoIndex] = true;
      }
    } else {
      video.pause();
      isPlayingRef.current[videoIndex] = false;
      const step = Math.min(absDiff, 0.05);
      video.currentTime = Math.max(0, currentTime - step);
    }

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [isLoaded, totalDuration, getVideoAndTime, getVideoRef]);

  // Inicia o loop de animação
  useEffect(() => {
    if (isLoaded) {
      animationFrameRef.current = requestAnimationFrame(animate);
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isLoaded, animate]);

  // Detecta quando o usuário começa a rolar — mostra quiz 0 imediatamente
  useEffect(() => {
    const handleFirstScroll = () => {
      if (!isInitializedRef.current) return;
      if (window.scrollY > 10 && !hasStartedScrolling && !firstQuizShownRef.current) {
        firstQuizShownRef.current = true;
        // Salva onde o intro está para continuar daqui depois
        if (introVideoRef.current) {
          introTimeAtQuizRef.current = introVideoRef.current.currentTime;
        }
        if (lockForQuizRef.current) lockForQuizRef.current(0);
        // Mantém scroll no topo — intro continua tocando por trás do overlay
        window.scrollTo(0, 0);
      }
    };
    window.addEventListener('scroll', handleFirstScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleFirstScroll);
  }, [hasStartedScrolling]);

  // Calcula os limites de tempo para cada transição
  const getTransitionBoundaries = useCallback(() => {
    const boundaries = [];
    let accumulated = 0;
    for (let i = 0; i < videoDurations.length; i++) {
      boundaries.push({
        start: accumulated,
        end: accumulated + videoDurations[i],
        index: i
      });
      accumulated += videoDurations[i];
    }
    return boundaries;
  }, [videoDurations]);

  // Troca a source de um vídeo dinamicamente (ex: baseado na resposta do quiz)
  const swapVideoSource = useCallback((videoIndex, newSrc) => {
    const videoRef = getVideoRef(videoIndex);
    const video = videoRef.current;
    if (!video) return;

    video.pause();
    isPlayingRef.current[videoIndex] = false;
    video.src = newSrc;
    video.load();

    // Atualiza duração quando o novo vídeo carregar
    const onLoaded = () => {
      setVideoDurations(prev => {
        const next = [...prev];
        next[videoIndex] = video.duration;
        return next;
      });
      setVideosReady(prev => {
        const next = [...prev];
        next[videoIndex] = true;
        return next;
      });
      video.removeEventListener('loadedmetadata', onLoaded);
    };
    video.addEventListener('loadedmetadata', onLoaded);
  }, [getVideoRef]);

  // === NOVO: Função para travar o scroll e mostrar quiz ===
  const lockForQuiz = useCallback((transitionIndex) => {
    isQuizLockedRef.current = true;
    setActiveQuizQuestion(transitionIndex);
  }, []);

  // Mantém a ref de lockForQuiz sempre atualizada
  lockForQuizRef.current = lockForQuiz;

  // === NOVO: Função chamada quando o usuário responde — destranca e avança ===
  // Usa snapToTimeRef para evitar dependência circular
  const unlockAndAdvance = useCallback(() => {
    const answeredTransition = allowedTransitionRef.current;
    isQuizLockedRef.current = false;
    setActiveQuizQuestion(-1);
    answeredQuestionsRef.current.add(answeredTransition);

    // Se era a última transição, marca quiz como completo
    if (answeredTransition >= DEFAULT_VIDEOS.length - 1) {
      setQuizCompleted(true);
      return;
    }

    // Caso especial: quiz 0 respondido antes do scroll começar
    // Pula transição 1 e vai direto pra transição 2 (dinâmica)
    if (answeredTransition === 0 && !hasStartedScrolling) {
      // CRÍTICO: setar refs ANTES de hasStartedScrolling para evitar race condition
      allowedTransitionRef.current = 1;
      isSnappingRef.current = true;

      // Pausa o intro e ativa o modo scroll
      if (introVideoRef.current) {
        introVideoRef.current.pause();
      }

      // Força o sistema a estar no vídeo 1 (índice 1) desde o início
      currentVideoIndexRef.current = 1;
      setCurrentVideoIndex(1);
      const video2 = video2Ref.current;
      if (video2) {
        video2.currentTime = 0;
      }

      // Seta targetTime para dentro do vídeo 1 (usando duração do vídeo 0 que NÃO mudou)
      const vid0Duration = videoDurationsRef.current[0] || 0;
      targetTimeRef.current = vid0Duration + 0.05;

      // Ativa scrolling DEPOIS de configurar tudo
      setHasStartedScrolling(true);

      // Snap da transição 2: usa valores FRESCOS (pós-swapVideoSource)
      // e fromTime para interpolar tempo diretamente, sem depender de scroll position
      setTimeout(() => {
        if (!snapToTimeRef.current) return;
        // Lê durações atuais (pode ter mudado pelo swapVideoSource)
        const freshDurations = videoDurationsRef.current;
        const d0 = freshDurations[0] || 0;
        const d1 = freshDurations[1] || 0;
        const freshStart = d0 + 0.05;
        const freshEnd = d0 + d1;
        snapToTimeRef.current(freshEnd, 1, 2500, freshStart);
      }, 300);
      return;
    }

    // Senão, avança para a próxima transição automaticamente
    const boundaries = getTransitionBoundaries();
    const nextTransition = answeredTransition + 1;
    if (boundaries[nextTransition]) {
      // Pequeno delay para a animação de saída do quiz, depois roda a transição
      setTimeout(() => {
        if (snapToTimeRef.current) {
          snapToTimeRef.current(boundaries[nextTransition].end, nextTransition, 2500);
        }
      }, 300);
    }
  }, [getTransitionBoundaries, hasStartedScrolling]);

  // === NOVO: Trigger nudge ===
  const triggerNudge = useCallback(() => {
    if (nudgeTimeoutRef.current) return; // Já está em nudge
    setShowNudge(true);
    nudgeTimeoutRef.current = setTimeout(() => {
      setShowNudge(false);
      nudgeTimeoutRef.current = null;
    }, 600);
  }, []);

  // Função para fazer snap suave para uma posição de tempo
  // fromTime: se fornecido, interpola targetTimeRef diretamente (evita desync scroll↔duração)
  const snapToTime = useCallback((targetTime, newAllowedTransition = null, duration = 2500, fromTime = null) => {
    if (!isLoaded || totalDuration === 0 || scrollHeight === 0) return;

    isSnappingRef.current = true;

    if (newAllowedTransition !== null) {
      allowedTransitionRef.current = newAllowedTransition;
    }

    const maxScroll = scrollHeight - window.innerHeight;

    // Se fromTime fornecido, reposiciona scroll corretamente antes de começar
    if (fromTime !== null) {
      const fromPercent = Math.min(fromTime / totalDuration, 1);
      const fromScroll = fromPercent * maxScroll;
      window.scrollTo(0, fromScroll);
      lastScrollYRef.current = fromScroll;
      targetTimeRef.current = fromTime;
    }

    const scrollPercent = Math.min(targetTime / totalDuration, 1);
    const targetScroll = scrollPercent * maxScroll;
    const startPosition = window.scrollY;
    const distance = targetScroll - startPosition;
    const snapStartTime = fromTime !== null ? fromTime : (startPosition / maxScroll) * totalDuration;
    const startTime = performance.now();

    const easeInOutCubic = (t) => {
      return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    const animateSnap = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutCubic(progress);
      const newScrollPos = startPosition + distance * easedProgress;
      window.scrollTo(0, newScrollPos);

      // Interpola tempo diretamente — nunca deriva do scroll position
      const interpolatedTime = snapStartTime + (targetTime - snapStartTime) * easedProgress;
      const newScrollPercent = Math.min(Math.max(newScrollPos / maxScroll, 0), 1);
      setScrollProgress(newScrollPercent);
      targetTimeRef.current = interpolatedTime;

      if (progress < 1) {
        requestAnimationFrame(animateSnap);
      } else {
        lastTransitionIndexRef.current = allowedTransitionRef.current;
        lastScrollYRef.current = window.scrollY;
        setTimeout(() => {
          isSnappingRef.current = false;
          // Após o snap terminar (transição chegou ao fim), decide: trava pra quiz ou auto-avança
          const currentAllowed = allowedTransitionRef.current;
          const boundaries = getTransitionBoundaries();
          const boundary = boundaries[currentAllowed];
          if (boundary) {
            const currentTargetTime = targetTimeRef.current;
            const progressInTransition = (currentTargetTime - boundary.start) / (boundary.end - boundary.start);
            if (progressInTransition >= 0.95) {
              // Se quiz dessa transição já foi respondido, auto-avança para a próxima
              if (answeredQuestionsRef.current.has(currentAllowed)) {
                if (currentAllowed < boundaries.length - 1) {
                  const nextTransition = currentAllowed + 1;
                  setTimeout(() => {
                    if (snapToTimeRef.current) {
                      snapToTimeRef.current(boundaries[nextTransition].end, nextTransition, 2500);
                    }
                  }, 100);
                }
              } else if (!isQuizLockedRef.current) {
                lockForQuiz(currentAllowed);
              }
            }
          }
        }, 50);
      }
    };

    requestAnimationFrame(animateSnap);
  }, [isLoaded, totalDuration, scrollHeight, getTransitionBoundaries, lockForQuiz]);

  // Mantém a ref de snapToTime sempre atualizada
  snapToTimeRef.current = snapToTime;

  // Efeito para atualizar o tempo alvo baseado no scroll com snap nas transições
  // MODIFICADO: bloqueia scroll quando quiz está ativo
  useEffect(() => {
    if (!isLoaded || totalDuration === 0 || !hasStartedScrolling) return;

    const boundaries = getTransitionBoundaries();
    if (boundaries.length === 0) return;

    const handleScroll = () => {
      // Se está no meio de um snap, bloqueia
      if (isSnappingRef.current) return;

      // === NOVO: Se quiz está travado, bloqueia scroll e dá nudge ===
      if (isQuizLockedRef.current) {
        triggerNudge();
        // Força o scroll de volta pra posição travada
        const currentAllowed = allowedTransitionRef.current;
        const boundary = boundaries[currentAllowed];
        if (boundary) {
          const maxScroll = scrollHeight - window.innerHeight;
          const lockedPercent = Math.min(boundary.end / totalDuration, 1);
          const lockedScroll = lockedPercent * maxScroll;
          window.scrollTo(0, lockedScroll);
        }
        return;
      }

      const scrollTop = window.scrollY;
      const scrollDelta = scrollTop - lastScrollYRef.current;

      if (Math.abs(scrollDelta) < 5) return;

      const direction = scrollDelta > 0 ? 'down' : 'up';
      const currentAllowed = allowedTransitionRef.current;
      const currentBoundary = boundaries[currentAllowed];

      if (!currentBoundary) {
        lastScrollYRef.current = scrollTop;
        return;
      }

      const transitionDuration = currentBoundary.end - currentBoundary.start;
      const currentTime = targetTimeRef.current;
      const timeInTransition = Math.max(0, Math.min(currentTime - currentBoundary.start, transitionDuration));
      const progressInTransition = timeInTransition / transitionDuration;

      if (direction === 'down') {
        const isAtEnd = progressInTransition >= 0.97;
        const canAdvance = currentAllowed < boundaries.length - 1;

        if (isAtEnd && canAdvance) {
          // Normalmente avançaria, mas agora precisa do quiz primeiro
          // Se não está travado, significa que já respondeu — pode avançar
          const nextTransition = currentAllowed + 1;
          snapToTime(boundaries[nextTransition].end, nextTransition, 2500);
        } else {
          snapToTime(currentBoundary.end, currentAllowed, 2500);
        }
      } else if (direction === 'up') {
        const isAtStart = progressInTransition <= 0.03;
        const canGoBack = currentAllowed > 0;

        if (isAtStart && canGoBack) {
          const prevTransition = currentAllowed - 1;
          snapToTime(boundaries[prevTransition].start, prevTransition, 2500);
        } else {
          snapToTime(currentBoundary.start, currentAllowed, 2500);
        }
      }

      lastScrollYRef.current = scrollTop;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    lastScrollYRef.current = window.scrollY;

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (snapTimeoutRef.current) {
        clearTimeout(snapTimeoutRef.current);
      }
    };
  }, [isLoaded, totalDuration, scrollHeight, hasStartedScrolling, getTransitionBoundaries, snapToTime, triggerNudge]);

  // Calcula posição dos marcadores
  const getMarkerPositions = useCallback(() => {
    if (totalDuration === 0) return [0, 33, 66, 100];
    let accumulated = 0;
    const positions = [];
    for (let i = 0; i < videoDurations.length; i++) {
      const position = (accumulated / totalDuration) * 100;
      positions.push(position);
      accumulated += videoDurations[i];
    }
    positions.push(100);
    return positions;
  }, [videoDurations, totalDuration]);

  // Handler para navegação ao clicar no marcador
  // MODIFICADO: respeita quiz lock
  const handleMarkerClick = useCallback((index) => {
    if (!isLoaded || totalDuration === 0 || scrollHeight === 0) return;
    if (isSnappingRef.current) return;
    if (isQuizLockedRef.current) {
      triggerNudge();
      return;
    }

    const boundaries = getTransitionBoundaries();

    if (!hasStartedScrolling) {
      setHasStartedScrolling(true);
      if (introVideoRef.current) {
        introVideoRef.current.pause();
      }
    }

    // Só permite ir até a transição permitida (não pular perguntas)
    const maxAllowed = allowedTransitionRef.current;
    const targetIndex = Math.min(index, maxAllowed);

    if (targetIndex >= videoDurations.length) {
      const lastIndex = videoDurations.length - 1;
      snapToTime(boundaries[lastIndex].end, lastIndex, 2500);
    } else {
      snapToTime(boundaries[targetIndex].start, targetIndex, 2500);
    }
  }, [isLoaded, totalDuration, scrollHeight, videoDurations, hasStartedScrolling, getTransitionBoundaries, snapToTime, triggerNudge]);

  // Handler para calcular a posição do scroll baseada na posição do arrasto na barra
  const calculateScrollFromTrackPosition = useCallback((clientY) => {
    if (!scrollTrackRef.current || !isLoaded || scrollHeight === 0 || totalDuration === 0) return;
    if (isQuizLockedRef.current) {
      triggerNudge();
      return;
    }

    const trackRect = scrollTrackRef.current.getBoundingClientRect();
    const trackHeight = trackRect.height;
    const relativeY = clientY - trackRect.top;
    const percentage = Math.min(Math.max(relativeY / trackHeight, 0), 1);
    const targetGlobalTime = percentage * totalDuration;

    const boundaries = getTransitionBoundaries();
    let targetTransitionIndex = 0;
    for (let i = 0; i < boundaries.length; i++) {
      if (targetGlobalTime >= boundaries[i].start && targetGlobalTime <= boundaries[i].end) {
        targetTransitionIndex = i;
        break;
      }
      if (targetGlobalTime > boundaries[i].end) {
        targetTransitionIndex = i;
      }
    }

    allowedTransitionRef.current = targetTransitionIndex;

    const maxScroll = scrollHeight - window.innerHeight;
    const targetScroll = percentage * maxScroll;
    window.scrollTo(0, targetScroll);
    setScrollProgress(percentage);
    targetTimeRef.current = targetGlobalTime;
    lastScrollYRef.current = targetScroll;
  }, [isLoaded, scrollHeight, totalDuration, getTransitionBoundaries, triggerNudge]);

  // Handlers para arrastar na barra (mouse)
  const handleTrackMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isQuizLockedRef.current) {
      triggerNudge();
      return;
    }
    if (!hasStartedScrolling) {
      setHasStartedScrolling(true);
      if (introVideoRef.current) {
        introVideoRef.current.pause();
      }
    }
    setIsDraggingTrack(true);
    calculateScrollFromTrackPosition(e.clientY);
  }, [hasStartedScrolling, calculateScrollFromTrackPosition, triggerNudge]);

  const handleTrackMouseMove = useCallback((e) => {
    if (!isDraggingTrack) return;
    e.preventDefault();
    calculateScrollFromTrackPosition(e.clientY);
  }, [isDraggingTrack, calculateScrollFromTrackPosition]);

  const handleTrackMouseUp = useCallback(() => {
    setIsDraggingTrack(false);
    if (isQuizLockedRef.current) return;
    const boundaries = getTransitionBoundaries();
    const currentAllowed = allowedTransitionRef.current;
    if (boundaries[currentAllowed]) {
      snapToTime(boundaries[currentAllowed].end, currentAllowed, 2500);
    }
  }, [getTransitionBoundaries, snapToTime]);

  // Handlers para arrastar na barra (touch)
  const handleTrackTouchStart = useCallback((e) => {
    e.stopPropagation();
    if (isQuizLockedRef.current) {
      triggerNudge();
      return;
    }
    if (!hasStartedScrolling) {
      setHasStartedScrolling(true);
      if (introVideoRef.current) {
        introVideoRef.current.pause();
      }
    }
    setIsDraggingTrack(true);
    if (e.touches.length > 0) {
      calculateScrollFromTrackPosition(e.touches[0].clientY);
    }
  }, [hasStartedScrolling, calculateScrollFromTrackPosition, triggerNudge]);

  const handleTrackTouchMove = useCallback((e) => {
    if (!isDraggingTrack) return;
    e.preventDefault();
    if (e.touches.length > 0) {
      calculateScrollFromTrackPosition(e.touches[0].clientY);
    }
  }, [isDraggingTrack, calculateScrollFromTrackPosition]);

  const handleTrackTouchEnd = useCallback(() => {
    setIsDraggingTrack(false);
    if (isQuizLockedRef.current) return;
    const boundaries = getTransitionBoundaries();
    const currentAllowed = allowedTransitionRef.current;
    if (boundaries[currentAllowed]) {
      snapToTime(boundaries[currentAllowed].end, currentAllowed, 2500);
    }
  }, [getTransitionBoundaries, snapToTime]);

  // Listeners globais de mouse/touch quando estiver arrastando
  useEffect(() => {
    if (isDraggingTrack) {
      window.addEventListener('mousemove', handleTrackMouseMove);
      window.addEventListener('mouseup', handleTrackMouseUp);
      window.addEventListener('touchmove', handleTrackTouchMove, { passive: false });
      window.addEventListener('touchend', handleTrackTouchEnd);
      return () => {
        window.removeEventListener('mousemove', handleTrackMouseMove);
        window.removeEventListener('mouseup', handleTrackMouseUp);
        window.removeEventListener('touchmove', handleTrackTouchMove);
        window.removeEventListener('touchend', handleTrackTouchEnd);
      };
    }
  }, [isDraggingTrack, handleTrackMouseMove, handleTrackMouseUp, handleTrackTouchMove, handleTrackTouchEnd]);

  // Função para ir até o FINAL de uma transição específica
  const scrollToEndOfTransition = useCallback((transitionIndex) => {
    if (!isLoaded || totalDuration === 0 || scrollHeight === 0) return;
    const boundaries = getTransitionBoundaries();
    if (!boundaries[transitionIndex]) return;
    const targetTime = boundaries[transitionIndex].end;
    snapToTime(targetTime, transitionIndex, 2500);
  }, [isLoaded, totalDuration, scrollHeight, getTransitionBoundaries, snapToTime]);

  // Handler para clique na tela - avança para próxima transição
  // MODIFICADO: respeita quiz lock
  const handleScreenClick = useCallback((e) => {
    if (e.target.tagName === 'BUTTON' || e.target.closest('button')) return;
    if (isSnappingRef.current) return;

    if (isQuizLockedRef.current) {
      triggerNudge();
      return;
    }

    if (!hasStartedScrolling) {
      if (!firstQuizShownRef.current) {
        firstQuizShownRef.current = true;
        if (introVideoRef.current) {
          introTimeAtQuizRef.current = introVideoRef.current.currentTime;
        }
        if (lockForQuizRef.current) lockForQuizRef.current(0);
      }
      return;
    }

    const currentAllowed = allowedTransitionRef.current;
    const nextTransitionIndex = Math.min(currentAllowed + 1, videoDurations.length - 1);

    if (currentAllowed === nextTransitionIndex) {
      scrollToEndOfTransition(currentAllowed);
    } else {
      scrollToEndOfTransition(nextTransitionIndex);
    }
  }, [hasStartedScrolling, videoDurations.length, scrollToEndOfTransition, triggerNudge]);

  // Cleanup do nudge timeout
  useEffect(() => {
    return () => {
      if (nudgeTimeoutRef.current) {
        clearTimeout(nudgeTimeoutRef.current);
      }
    };
  }, []);

  return {
    // Refs
    video1Ref,
    video2Ref,
    video3Ref,
    introVideoRef,
    scrollTrackRef,
    // State
    scrollProgress,
    isLoaded,
    hasStartedScrolling,
    introVideoLoaded,
    setIntroVideoLoaded,
    currentVideoIndex,
    previousVideoIndex,
    scrollHeight,
    videoDurations,
    totalDuration,
    // Quiz integration
    activeQuizQuestion,
    showNudge,
    quizCompleted,
    unlockAndAdvance,
    // Handlers
    handleLoadedMetadata,
    handleCanPlayThrough,
    handleMarkerClick,
    handleTrackMouseDown,
    handleTrackTouchStart,
    handleScreenClick,
    getMarkerPositions,
    // Constants
    VIDEOS: DEFAULT_VIDEOS,
    swapVideoSource,
  };
}
